import {
  AlertTriangle,
  Database,
  ExternalLink,
  Globe,
  KeyRound,
  Train,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Github } from "@/components/icons/github";
import { ProjectResetDbPasswordDialog } from "@/components/project-reset-db-password-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  deleteAllProjectResources,
  deleteProject,
  deleteProjectCustomDomain,
  deleteProjectGithub,
  deleteProjectRailway,
  deleteProjectSupabase,
  isApiError,
  type ProjectSummary,
  type ResourceDeleteResponse,
  unlinkProjectResource,
  useDeleteAdminProject,
  useProjectResourceStatus,
} from "@/lib/api";
import type { ResourceStatusMap } from "@/lib/local-types";
import { logger } from "@/lib/logger";
import { getLinkedResourceNames } from "@/lib/project-status";

interface ProjectResourcesPanelProps {
  project: ProjectSummary;
  /** Owner or admin: shows the delete, "Mark as removed" and remove controls. */
  canManage: boolean;
  onResourceDeleted: (updates: Partial<ProjectSummary>) => void;
  onProjectDeleted: () => void;
  /**
   * Who is tearing the project down, and so how the record goes.
   *
   * - `owner` (the project-detail dialog): "Delete all" removes the resources,
   *   and "Remove Project" then removes the record through `deleteProject`.
   * - `admin` (the admin Projects page, BOIE-338): "Delete all" goes on to
   *   remove the record through `adminDeleteProject` once every resource is
   *   gone, so one confirmed action tears the project down. A resource that
   *   fails is named and the record is kept.
   *
   * Both record routes refuse while a resource is still linked.
   */
  variant?: "owner" | "admin";
  /**
   * Told whenever a deletion, unlink or record removal starts or ends, so a
   * surrounding dialog can stay open until its outcome is shown.
   */
  onBusyChange?: (busy: boolean) => void;
}

/**
 * The teardown section of a project: its connected resources, each with its
 * own delete, plus "Delete all" and the record removal.
 *
 * Shared by the project-detail dialog's Resources tab and the admin Projects
 * page's delete dialog, so both run the same per-resource teardown and report
 * the same per-resource outcomes.
 */
export function ProjectResourcesPanel({
  project,
  canManage,
  onResourceDeleted,
  onProjectDeleted,
  variant = "owner",
  onBusyChange,
}: ProjectResourcesPanelProps) {
  // Sourced from TanStack Query (not a local fetch) so that
  // useResetProjectDbPassword's invalidation of queryKeys.projectResourceStatus
  // actually refreshes this view -- e.g. the secrets-not-configured banner
  // below needs to clear once the reset dialog configures the CI secrets.
  const { data: liveStatus, isLoading: statusLoading } = useProjectResourceStatus(project.id);
  const deleteAdminProject = useDeleteAdminProject();
  const [deletingResource, setDeletingResource] = useState<string | null>(null);
  const [error, setError] = useState<{
    message: string;
    manualUrl?: string;
    resourceKey?: string;
    urlFields?: Partial<ProjectSummary>;
  } | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [customDomainRemoved, setCustomDomainRemoved] = useState(false);
  const [resetDbPasswordOpen, setResetDbPasswordOpen] = useState(false);
  const isAdminDelete = variant === "admin";

  // Tri-state (BOIE-124): only `false` (confirmed unset) warns. `null`/
  // `undefined` (unknown or legacy projects predating this field) stays quiet.
  const secretsNotConfigured = liveStatus?.supabase_secrets_configured === false;
  // Same tri-state (BOIE-267): only an advanced scaffold whose Codex reviewer
  // is confirmed to lack its key warns.
  const reviewerKeyMissing = liveStatus?.pr_reviewer_configured === false;

  const customDomainUrl = project.custom_subdomain
    ? project.app_url && !project.app_url.includes("railway.app")
      ? project.app_url
      : null
    : null;

  const hasCustomDomain = !!project.custom_subdomain || customDomainRemoved;

  // Listed in the same order the backend tears them down (custom domain needs
  // Railway's data; Railway goes before GitHub so the CD link isn't orphaned).
  // "Delete all" relies on that order when merging the per-resource outcomes.
  const resources = [
    ...(hasCustomDomain
      ? [
          {
            key: "custom-domain",
            name: "Custom Domain",
            icon: Globe,
            url: customDomainUrl,
            included: true,
            urlFields: {} as Partial<ProjectSummary>,
          },
        ]
      : []),
    {
      key: "railway",
      name: "Railway",
      icon: Train,
      url: project.railway_dashboard_url,
      included: project.include_railway,
      urlFields: {
        railway_dashboard_url: null,
        app_url: null,
        custom_subdomain: null,
      } as Partial<ProjectSummary>,
    },
    {
      key: "supabase",
      name: "Supabase",
      icon: Database,
      url: project.supabase_dashboard_url,
      included: project.include_supabase,
      urlFields: { supabase_dashboard_url: null } as Partial<ProjectSummary>,
    },
    {
      key: "github",
      name: "GitHub Repository",
      icon: Github,
      url: project.github_url,
      included: true, // GitHub is always included
      urlFields: { github_url: null } as Partial<ProjectSummary>,
    },
  ];

  const deleteResourceByKey = async (
    resourceKey: string,
  ): Promise<ResourceDeleteResponse | undefined> => {
    const path = { project_id: project.id };
    switch (resourceKey) {
      case "custom-domain": {
        const { data } = await deleteProjectCustomDomain({ path });
        return data;
      }
      case "railway": {
        const { data } = await deleteProjectRailway({ path });
        return data;
      }
      case "supabase": {
        const { data } = await deleteProjectSupabase({ path });
        return data;
      }
      case "github": {
        const { data } = await deleteProjectGithub({ path });
        return data;
      }
      default:
        throw new Error(`Unknown resource: ${resourceKey}`);
    }
  };

  /**
   * Remove the project record through this panel's route: the owner's
   * `deleteProject` or the admin's `adminDeleteProject`. Both refuse while a
   * resource is still linked.
   *
   * Returns null once the record is gone, else the reason it is not.
   */
  const removeRecord = async (): Promise<string | null> => {
    try {
      if (isAdminDelete) {
        await deleteAdminProject.mutateAsync({ projectId: project.id });
        return null;
      }
      const { data: result } = await deleteProject({ path: { project_id: project.id } });
      return result?.success ? null : result?.error || "Failed to delete project";
    } catch (err) {
      logger.error("[project-resources] remove project failed:", err);
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      return detail || "Failed to delete project";
    }
  };

  const handleDeleteResource = async (resourceKey: string, urlFields: Partial<ProjectSummary>) => {
    setDeletingResource(resourceKey);
    setError(null);
    try {
      const result = await deleteResourceByKey(resourceKey);
      if (result?.success) {
        if (resourceKey === "custom-domain") {
          setCustomDomainRemoved(true);
          onResourceDeleted({
            custom_subdomain: null,
            ...(result.updated_app_url ? { app_url: result.updated_app_url } : {}),
          });
        } else {
          onResourceDeleted(urlFields);
        }
      } else {
        setError({
          message: result?.error || "Failed to delete resource",
          manualUrl: result?.manual_url || undefined,
          resourceKey,
          urlFields,
        });
      }
    } catch (err) {
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      setError({
        message: detail || "Failed to delete resource",
        resourceKey,
        urlFields,
      });
    } finally {
      setDeletingResource(null);
    }
  };

  /**
   * Tear down every connected resource in one call.
   *
   * The backend keeps going when a step fails, so the outcomes are merged
   * per-resource: everything that succeeded is reflected in the UI even when
   * something else failed, and the failures are surfaced so they can be retried
   * or marked as removed from their own row. An admin delete then removes the
   * record, but only when every resource came down.
   */
  const handleDeleteAll = async () => {
    setDeletingAll(true);
    setError(null);
    try {
      const { data: result } = await deleteAllProjectResources({
        path: { project_id: project.id },
      });
      const outcomes = result?.results ?? {};

      // `resources` is in the backend's teardown order, so later steps
      // correctly overwrite earlier ones (Railway nulls the app_url that the
      // custom-domain step had just reverted to).
      let updates: Partial<ProjectSummary> = {};
      for (const resource of resources) {
        const outcome = outcomes[resource.key];
        if (!outcome?.success) {
          continue;
        }
        if (resource.key === "custom-domain") {
          setCustomDomainRemoved(true);
          updates = {
            ...updates,
            custom_subdomain: null,
            ...(outcome.updated_app_url ? { app_url: outcome.updated_app_url } : {}),
          };
        } else {
          updates = { ...updates, ...resource.urlFields };
        }
      }
      if (Object.keys(updates).length > 0) {
        onResourceDeleted(updates);
      }

      if (!result?.success) {
        const failures = resources
          .map((resource) => ({ name: resource.name, outcome: outcomes[resource.key] }))
          .filter(({ outcome }) => outcome && !outcome.success)
          .map(({ name, outcome }) => `${name}: ${outcome?.error || "deletion failed"}`);
        const summary = failures.length
          ? `Some resources could not be deleted. ${failures.join("; ")}`
          : "Failed to delete resources";
        setError({
          message: isAdminDelete
            ? `${summary.replace(/\.$/, "")}. The project record was kept.`
            : summary,
        });
        return;
      }

      if (isAdminDelete) {
        const failure = await removeRecord();
        if (failure) {
          setError({
            message: `Every resource was deleted, but the project record was not removed: ${failure}`,
          });
          return;
        }
        onProjectDeleted();
      }
    } catch (err) {
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      setError({ message: detail || "Failed to delete resources" });
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteProject = async () => {
    setDeletingProject(true);
    setError(null);
    try {
      const failure = await removeRecord();
      if (failure) {
        setError({ message: failure });
        return;
      }
      onProjectDeleted();
    } finally {
      setDeletingProject(false);
    }
  };

  const handleUnlink = async (resourceKey: string, urlFields: Partial<ProjectSummary>) => {
    setUnlinking(true);
    setError(null);
    try {
      const { data: result } = await unlinkProjectResource({
        path: { project_id: project.id, resource_type: resourceKey },
      });
      if (result?.success) {
        onResourceDeleted(urlFields);
      } else {
        setError({ message: result?.error || "Failed to unlink resource" });
      }
    } catch (err) {
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      setError({ message: detail || "Failed to unlink resource" });
    } finally {
      setUnlinking(false);
    }
  };

  // Gated on the same fields the backend checks, so the confirmation dialog
  // names exactly what "Delete all" will attempt.
  const linkedResourceNames = getLinkedResourceNames(project);
  const allResourcesRemoved = linkedResourceNames.length === 0;

  const busy = deletingAll || deletingResource !== null;
  // Any request whose outcome this panel still has to show.
  const working = busy || deletingProject || unlinking;

  useEffect(() => {
    onBusyChange?.(working);
  }, [working, onBusyChange]);

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground text-sm">Checking resource status...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info banner — only relevant when both Railway and GitHub are still connected */}
      {project.railway_dashboard_url && project.github_url && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500 dark:bg-amber-950">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-800 dark:font-medium dark:text-amber-200">
            Delete Railway before GitHub to avoid broken CD connections.
          </p>
        </div>
      )}

      {/* Delete-all action: saves tearing resources down one at a time */}
      {canManage && linkedResourceNames.length > 0 && (
        <div className="flex justify-end">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="text-destructive hover:!bg-destructive/10 hover:!text-destructive"
                disabled={busy}
              >
                <Trash2 className="mr-1 size-3.5" />
                {deletingAll
                  ? "Deleting all..."
                  : isAdminDelete
                    ? "Delete all and remove project"
                    : "Delete all"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {isAdminDelete
                    ? "Delete all resources and the project?"
                    : "Delete all resources?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {isAdminDelete
                    ? `This will permanently delete ${linkedResourceNames.join(", ")}, then remove the project record. If a resource cannot be deleted, the record is kept. This action cannot be undone.`
                    : `This will permanently delete ${linkedResourceNames.join(", ")}. This action cannot be undone.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleDeleteAll}
                >
                  {isAdminDelete ? "Delete all and remove" : "Delete all"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Resource rows */}
      <div className="space-y-2">
        {resources.map((resource) => {
          const live = liveStatus?.[resource.key as keyof ResourceStatusMap];
          const isMissing = live === "missing" && !!resource.url;
          const isOrphanedCustomDomain =
            resource.key === "custom-domain" && !resource.url && !!project.custom_subdomain;
          const resourceStatus = isMissing
            ? "missing"
            : resource.url
              ? "connected"
              : isOrphanedCustomDomain
                ? "orphaned"
                : resource.included
                  ? "removed"
                  : "not_provisioned";

          const showSecretsBanner =
            resource.key === "supabase" &&
            resource.included &&
            !!resource.url &&
            !isMissing &&
            secretsNotConfigured &&
            canManage;

          const showReviewerKeyNotice =
            resource.key === "github" && !!resource.url && !isMissing && reviewerKeyMissing;

          return (
            <div key={resource.key} className="space-y-2">
              <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                <div className="flex items-center gap-3">
                  <resource.icon className="text-muted-foreground size-5" />
                  <div>
                    <p className="text-sm font-medium">{resource.name}</p>
                    <p className="text-muted-foreground text-xs">
                      {resourceStatus === "connected" && (
                        <span className="text-green-600 dark:text-green-400">Connected</span>
                      )}
                      {resourceStatus === "missing" && (
                        <span className="text-amber-600 dark:text-amber-400">
                          Deleted externally
                        </span>
                      )}
                      {resourceStatus === "orphaned" && (
                        <span className="text-amber-600 dark:text-amber-400">
                          DNS records remain
                        </span>
                      )}
                      {resourceStatus === "removed" && (
                        <span className="text-muted-foreground">Removed</span>
                      )}
                      {resourceStatus === "not_provisioned" && (
                        <span className="text-muted-foreground">Not provisioned</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {resource.url && !isMissing && (
                    <a href={resource.url} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="ghost">
                        <ExternalLink className="mr-1 size-3.5" />
                        Open
                      </Button>
                    </a>
                  )}
                  {canManage && isMissing && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={unlinking || busy}
                      onClick={() => handleUnlink(resource.key, resource.urlFields)}
                    >
                      {unlinking ? "Unlinking..." : "Mark as removed"}
                    </Button>
                  )}
                  {resource.key === "supabase" && canManage && resource.url && !isMissing && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setResetDbPasswordOpen(true)}
                    >
                      <KeyRound className="mr-1 size-3.5" />
                      Reset password
                    </Button>
                  )}
                  {canManage && (resource.url || isOrphanedCustomDomain) && !isMissing && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:!bg-destructive/10 hover:!text-destructive"
                          disabled={busy}
                        >
                          <Trash2 className="mr-1 size-3.5" />
                          {deletingResource === resource.key || deletingAll
                            ? "Deleting..."
                            : "Delete"}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {resource.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete the {resource.name} resource. This action
                            cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => handleDeleteResource(resource.key, resource.urlFields)}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>

              {showSecretsBanner && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600 dark:text-red-400" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div>
                      <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                        CI migration secrets not configured
                      </p>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        Supabase migrations will fail on the next push to{" "}
                        <code className="font-mono">main</code> until the{" "}
                        <code className="font-mono">supabase</code> GitHub environment secrets are
                        set.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setResetDbPasswordOpen(true)}
                    >
                      <KeyRound className="mr-1 size-3.5" />
                      Configure CI secrets
                    </Button>
                    <p className="text-xs text-red-700/80 dark:text-red-300/80">
                      Or set them manually under the repo&apos;s Settings &rarr; Environments &rarr;
                      supabase.
                    </p>
                  </div>
                </div>
              )}

              {showReviewerKeyNotice && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                      Codex PR review has no key
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      The repo&apos;s PR reviewer fails until its{" "}
                      <code className="font-mono">OPENAI_API_KEY</code> repository secret is set.
                      Set it under the repo&apos;s Settings &rarr; Secrets and variables &rarr;
                      Actions. Or an admin sets{" "}
                      <code className="font-mono">PR_REVIEWER_OPENAI_API_KEY</code> on the
                      ScaffoldUI backend if it has none, then runs <strong>Check repo setup</strong>{" "}
                      and <strong>Apply fixes</strong> on the admin Projects page.
                    </p>
                    <p className="mt-1 text-xs text-amber-700/80 dark:text-amber-300/80">
                      {isAdminDelete
                        ? "This notice stays, even once the secret is set, until you run Check repo setup on the admin Projects page: the check records what it sees on GitHub."
                        : "This notice stays, even once you set the secret, until an admin runs Check repo setup: ScaffoldUI only learns about the secret from that check."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm text-red-700 dark:text-red-200">{error.message}</p>
          <div className="mt-2 flex items-center gap-3">
            {error.manualUrl && (
              <a
                href={error.manualUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-red-700 underline dark:text-red-200"
              >
                Delete manually <ExternalLink className="size-3" />
              </a>
            )}
            {error.resourceKey && error.urlFields && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleUnlink(error.resourceKey!, error.urlFields!)}
                disabled={unlinking}
              >
                {unlinking ? "Unlinking..." : "Mark as removed"}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Remove Project button */}
      {canManage && (
        <div className="border-t pt-4">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                disabled={!allResourcesRemoved || deletingProject || busy}
              >
                <Trash2 className="mr-2 size-4" />
                {deletingProject ? "Removing..." : "Remove Project"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove project?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove this project record. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleDeleteProject}
                >
                  Remove Project
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {!allResourcesRemoved && (
            <p className="text-muted-foreground mt-2 text-xs">
              Delete all connected resources before removing the project.
            </p>
          )}
        </div>
      )}

      <ProjectResetDbPasswordDialog
        projectId={project.id}
        projectName={project.project_name}
        open={resetDbPasswordOpen}
        onOpenChange={setResetDbPasswordOpen}
      />
    </div>
  );
}
